import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, Volume2, VolumeX, Minimize2, Maximize2, SwitchCamera } from "lucide-react";
import { toast } from "sonner";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { callBus } from "./call-bus";
import { useHistoryBackClose } from "@/hooks/use-history-back-close";

type Mode = "audio" | "video";
type StartOpts = { peerId: string; peerName: string; peerAvatar: string | null; mode: Mode; roomId?: string };
export type CallCenterHandle = { start: (opts: StartOpts) => void };

type Me = { id: string; name: string; avatar: string | null } | null;

type Phase = "idle" | "permission" | "outgoing" | "incoming" | "active";

type CallInfo = {
  callId: string;
  peerId: string;
  peerName: string;
  peerAvatar: string | null;
  mode: Mode;
  isCaller: boolean;
  pendingOffer?: RTCSessionDescriptionInit;
};

type PendingAction = { kind: "outgoing"; opts: StartOpts } | { kind: "incoming" };

function initialsOf(name: string) {
  return name.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U";
}

function humanizeMediaError(err: unknown): Error {
  const name = (err as DOMException)?.name || "";
  const map: Record<string, string> = {
    NotAllowedError: "Izin mic/kamera ditolak. Klik ikon gembok di address bar → izinkan Microphone/Camera, lalu muat ulang halaman.",
    SecurityError: "Akses mic/kamera diblokir oleh browser (butuh HTTPS & izin situs).",
    NotFoundError: "Perangkat mic/kamera tidak ditemukan di device ini.",
    NotReadableError: "Mic/kamera sedang dipakai aplikasi lain. Tutup aplikasi tersebut lalu coba lagi.",
    OverconstrainedError: "Perangkat mic/kamera tidak memenuhi kualitas yang diminta.",
    AbortError: "Akses mic/kamera dibatalkan.",
    TypeError: "Panggilan tidak didukung di konteks ini (butuh HTTPS).",
  };
  return new Error(map[name] || (err instanceof Error ? err.message : String(err)));
}

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] },
  ],
};

async function waitIceComplete(pc: RTCPeerConnection, timeoutMs = 4000) {
  if (pc.iceGatheringState === "complete") return;
  await new Promise<void>((resolve) => {
    let done = false;
    const finish = () => { if (done) return; done = true; pc.removeEventListener("icegatheringstatechange", check); resolve(); };
    const check = () => { if (pc.iceGatheringState === "complete") finish(); };
    pc.addEventListener("icegatheringstatechange", check);
    setTimeout(finish, timeoutMs);
  });
}

export const CallCenter = forwardRef<CallCenterHandle, { me: Me }>(function CallCenter({ me }, ref) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [info, setInfo] = useState<CallInfo | null>(null);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const facingModeRef = useRef<"user" | "environment">("user");
  const [switchingCam, setSwitchingCam] = useState(false);
  const phaseRef = useRef<Phase>("idle");
  const infoRef = useRef<CallInfo | null>(null);
  // Sync setters — update ref immediately so broadcast handlers see the
  // latest value without waiting for a useEffect commit (fixes stale
  // "busy" auto-rejects right after a call ends).
  const applyPhase = useCallback((p: Phase) => { phaseRef.current = p; setPhase(p); }, []);
  const applyInfo = useCallback((i: CallInfo | null) => { infoRef.current = i; setInfo(i); }, []);

  // Broadcast "call is showing an on-screen surface" so the app shell can
  // hide the mobile bottom nav (chat should feel full-screen during a call,
  // even when the call is minimized to a floating pill).
  useEffect(() => {
    const showing = phase !== "idle";
    callBus.setActive(showing);
    return () => { if (showing) callBus.setActive(false); };
  }, [phase]);

  // Broadcast minimized state so the shell can re-show the bottom nav
  // while the call is minimized to a floating pill.
  useEffect(() => {
    callBus.setMinimized(minimized);
  }, [minimized]);

  // Back button / swipe-back while the full-screen call is open → minimize it
  // instead of leaving the page (WhatsApp-style).
  useHistoryBackClose(phase === "active" && !minimized, () => setMinimized(true));


  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const inboxRef = useRef<RealtimeChannel | null>(null);
  const outCache = useRef<Map<string, RealtimeChannel>>(new Map());
  const startedAtRef = useRef<number>(0);
  const timerRef = useRef<number | null>(null);
  const disconnectTimerRef = useRef<number | null>(null);
  const ringRef = useRef<HTMLAudioElement | null>(null);
  // Simpan invite terakhir supaya bisa di-broadcast ulang selama fase outgoing.
  // Berguna ketika penerima membuka app dari notifikasi (realtime baru connect
  // setelah invite pertama sudah lewat) — kita kirim ulang sampai dijawab/ditolak/batal.
  const lastInviteRef = useRef<{ peerId: string; payload: Record<string, unknown> } | null>(null);
  const rebroadcastTimerRef = useRef<number | null>(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
    if (disconnectTimerRef.current) { window.clearTimeout(disconnectTimerRef.current); disconnectTimerRef.current = null; }
    if (rebroadcastTimerRef.current) { window.clearInterval(rebroadcastTimerRef.current); rebroadcastTimerRef.current = null; }
    lastInviteRef.current = null;
    if (pcRef.current) {
      try {
        pcRef.current.onconnectionstatechange = null;
        pcRef.current.ontrack = null;
        pcRef.current.close();
      } catch { /* noop */ }
      pcRef.current = null;
    }
    if (localStreamRef.current) { localStreamRef.current.getTracks().forEach((t) => t.stop()); localStreamRef.current = null; }
    remoteStreamRef.current = null;
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    if (ringRef.current) { try { ringRef.current.pause(); } catch { /* noop */ } }
    setMuted(false); setCamOff(false); setElapsed(0); setSpeakerOn(true); setMinimized(false);
  }, []);

  const endAll = useCallback(() => { cleanup(); setPending(null); applyPhase("idle"); applyInfo(null); }, [cleanup, applyPhase, applyInfo]);

  const getOutChannel = useCallback(async (peerId: string): Promise<RealtimeChannel> => {
    const cached = outCache.current.get(peerId);
    if (cached) return cached;
    const ch = supabase.channel(`call-inbox-${peerId}`, { config: { broadcast: { self: false, ack: false } } });
    await new Promise<void>((resolve) => {
      ch.subscribe((status) => { if (status === "SUBSCRIBED") resolve(); });
      setTimeout(resolve, 2000);
    });
    outCache.current.set(peerId, ch);
    return ch;
  }, []);

  const sendSignal = useCallback(async (toUserId: string, event: string, payload: Record<string, unknown>) => {
    const ch = await getOutChannel(toUserId);
    await ch.send({ type: "broadcast", event, payload });
  }, [getOutChannel]);

  const attachRemote = useCallback((stream: MediaStream, mode: Mode) => {
    remoteStreamRef.current = stream;
    if (mode === "video" && remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = stream;
      remoteVideoRef.current.play().catch(() => { /* noop */ });
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = stream;
      remoteAudioRef.current.play().catch(() => { /* noop */ });
    }
  }, []);

  // Re-attach remote stream whenever the audio/video elements (re)mount.
  // Needed for: incoming-call answered via permission dialog (audio element
  // wasn't in the DOM when ontrack fired), and minimize/maximize transitions.
  useEffect(() => {
    const s = remoteStreamRef.current;
    if (!s) return;
    const a = remoteAudioRef.current;
    if (a && a.srcObject !== s) {
      a.srcObject = s;
      a.play().catch(() => { /* noop */ });
    }
    const v = remoteVideoRef.current;
    if (v && v.srcObject !== s) {
      v.srcObject = s;
      v.play().catch(() => { /* noop */ });
    }
    // Also re-attach local video preview (video-mode minimize/maximize)
    const ls = localStreamRef.current;
    const lv = localVideoRef.current;
    if (ls && lv && lv.srcObject !== ls) {
      lv.srcObject = ls;
      lv.play().catch(() => { /* noop */ });
    }
  }, [phase, minimized]);


  const buildPc = useCallback((mode: Mode, callId: string) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    const remote = new MediaStream();
    pc.ontrack = (e) => {
      e.streams[0]?.getTracks().forEach((t) => remote.addTrack(t));
      if (!e.streams.length) remote.addTrack(e.track);
      attachRemote(remote, mode);
    };
    pc.onconnectionstatechange = () => {
      if (pcRef.current !== pc || infoRef.current?.callId !== callId) return;
      const st = pc.connectionState;
      if (st === "connected" && disconnectTimerRef.current) {
        window.clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      if (st === "failed") {
        toast.info("Panggilan berakhir");
        endAll();
      }
      if (st === "disconnected" && !disconnectTimerRef.current) {
        disconnectTimerRef.current = window.setTimeout(() => {
          if (pcRef.current !== pc || infoRef.current?.callId !== callId) return;
          if (pc.connectionState === "disconnected" || pc.connectionState === "failed") {
            toast.info("Panggilan berakhir");
            endAll();
          }
        }, 8000);
      }
    };
    return pc;
  }, [attachRemote, endAll]);

  const getMedia = useCallback(async (mode: Mode) => {
    if (!window.isSecureContext) {
      throw new Error("Butuh koneksi HTTPS untuk mengakses mic/kamera. Buka lewat domain https, bukan http.");
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Browser tidak mendukung akses mic/kamera. Coba Chrome/Safari terbaru.");
    }
    const askVideo = mode === "video";
    const constraints: MediaStreamConstraints = askVideo
      ? { audio: true, video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: facingModeRef.current } }
      : { audio: true, video: false };
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      const name = (err as DOMException)?.name || "";
      // Fallback: video mode but camera missing/blocked → try audio-only so panggilan tetap jalan
      if (askVideo && (name === "NotFoundError" || name === "OverconstrainedError" || name === "NotReadableError")) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          toast.info("Kamera tidak tersedia, panggilan dilanjutkan sebagai suara saja.");
        } catch (err2) {
          throw humanizeMediaError(err2);
        }
      } else {
        throw humanizeMediaError(err);
      }
    }
    localStreamRef.current = stream;
    if (mode === "video" && localVideoRef.current && stream.getVideoTracks().length > 0) {
      localVideoRef.current.srcObject = stream;
      localVideoRef.current.play().catch(() => { /* noop */ });
    }
    return stream;
  }, []);

  const startTimer = useCallback(() => {
    startedAtRef.current = Date.now();
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => setElapsed(Date.now() - startedAtRef.current), 500);
  }, []);

  const recordCallActivity = useCallback(async (opts: StartOpts) => {
    if (!me || !opts.roomId) return;
    const icon = opts.mode === "video" ? "📹" : "📞";
    const kind = opts.mode === "video" ? "video" : "suara";
    await supabase.from("chat_messages").insert({
      room_id: opts.roomId,
      user_id: me.id,
      content: `${icon} Panggilan ${kind} dari ${me.name}`,
    });
  }, [me]);

  const GRANT_KEY = "callcenter.mediaGranted.v1";
  const grantedRef = useRef<boolean>(
    typeof window !== "undefined" && window.localStorage?.getItem(GRANT_KEY) === "1"
  );
  const markGranted = useCallback(() => {
    grantedRef.current = true;
    try { window.localStorage.setItem(GRANT_KEY, "1"); } catch { /* noop */ }
  }, []);

  // Probe Permissions API to skip the primer entirely if the browser
  // already remembers a granted mic permission (works on Chrome/Edge/Android).
  useEffect(() => {
    const perms = (navigator as Navigator & { permissions?: { query: (d: { name: PermissionName }) => Promise<PermissionStatus> } }).permissions;
    if (!perms?.query) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await perms.query({ name: "microphone" as PermissionName });
        if (cancelled) return;
        if (status.state === "granted") markGranted();
        status.onchange = () => { if (status.state === "granted") markGranted(); };
      } catch { /* not supported — fall back to primer once */ }
    })();
    return () => { cancelled = true; };
  }, [markGranted]);

  // Actual outgoing flow (runs after permission granted)
  const runOutgoing = useCallback(async (opts: StartOpts, callId: string) => {
    if (!me) return;
    try {
      const stream = await getMedia(opts.mode);
      markGranted();
      const pc = buildPc(opts.mode, callId);
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: opts.mode === "video" });
      await pc.setLocalDescription(offer);
      await waitIceComplete(pc);
      const invitePayload: Record<string, unknown> = {
        callId, mode: opts.mode,
        from: me.id, fromName: me.name, fromAvatar: me.avatar,
        sdp: pc.localDescription,
      };
      await sendSignal(opts.peerId, "invite", invitePayload);
      lastInviteRef.current = { peerId: opts.peerId, payload: invitePayload };
      // Broadcast ulang selama fase outgoing supaya penerima yang baru buka app
      // (dari notifikasi/cold start) tetap dapat invite dan UI incoming muncul.
      if (rebroadcastTimerRef.current) window.clearInterval(rebroadcastTimerRef.current);
      rebroadcastTimerRef.current = window.setInterval(() => {
        const inv = lastInviteRef.current;
        if (!inv || phaseRef.current !== "outgoing" || infoRef.current?.callId !== callId) {
          if (rebroadcastTimerRef.current) { window.clearInterval(rebroadcastTimerRef.current); rebroadcastTimerRef.current = null; }
          return;
        }
        void sendSignal(inv.peerId, "invite", inv.payload).catch(() => { /* noop */ });
      }, 3000);
      void recordCallActivity(opts).catch(() => { /* riwayat panggilan best-effort */ });
      // Kirim push notification supaya penerima tetap terima notif walau app di background
      try {
        void supabase.functions.invoke("send-fcm-notification", {
          body: {
            type: "INSERT",
            table: "calls",
            record: {
              id: callId,
              peer_id: opts.peerId,
              caller_name: me.name,
              mode: opts.mode,
              chat_room_id: opts.roomId,
            },
          },
        });
      } catch { /* noop — push adalah best-effort */ }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error("Gagal memulai panggilan: " + msg);
      if (infoRef.current?.callId === callId) endAll();
    }
  }, [me, getMedia, buildPc, sendSignal, recordCallActivity, endAll]);

  // Outgoing call trigger from parent — shows permission primer first
  useImperativeHandle(ref, () => ({
    start: (opts) => {
      if (!me) { toast.error("Belum siap"); return; }
      // Use ref (not stale closure) to check current phase
      if (phaseRef.current !== "idle") { toast.error("Sedang dalam panggilan lain"); return; }
      // Defensive: make absolutely sure previous call state is cleared
      cleanup();
      setPending(null);
      const callId = `${me.id}-${opts.peerId}-${Date.now()}`;
      applyInfo({ callId, peerId: opts.peerId, peerName: opts.peerName, peerAvatar: opts.peerAvatar, mode: opts.mode, isCaller: true });
      if (grantedRef.current) {
        applyPhase("outgoing");
        void runOutgoing(opts, callId);
      } else {
        setPending({ kind: "outgoing", opts });
        applyPhase("permission");
      }
    },
  }), [me, cleanup, runOutgoing, applyInfo, applyPhase]);

  // Inbox subscription
  useEffect(() => {
    if (!me) return;
    const ch = supabase.channel(`call-inbox-${me.id}`, { config: { broadcast: { self: false, ack: false } } });
    inboxRef.current = ch;

    ch.on("broadcast", { event: "invite" }, ({ payload }) => {
      const p = payload as { callId: string; mode: Mode; from: string; fromName: string; fromAvatar: string | null; sdp: RTCSessionDescriptionInit };
      if (phaseRef.current !== "idle") {
        if (infoRef.current?.callId !== p.callId) {
          void sendSignal(p.from, "reject", { callId: p.callId, reason: "busy" });
        }
        return;
      }
      applyInfo({ callId: p.callId, peerId: p.from, peerName: p.fromName, peerAvatar: p.fromAvatar, mode: p.mode, isCaller: false, pendingOffer: p.sdp });
      applyPhase("incoming");
      try { ringRef.current?.play().catch(() => { /* noop */ }); } catch { /* noop */ }
    });

    ch.on("broadcast", { event: "answer" }, async ({ payload }) => {
      const p = payload as { callId: string; sdp: RTCSessionDescriptionInit };
      const cur = infoRef.current;
      if (!pcRef.current || !cur || p.callId !== cur.callId) return;
      try {
        await pcRef.current.setRemoteDescription(p.sdp);
        applyPhase("active");
        startTimer();
      } catch {
        toast.error("Gagal menyambungkan panggilan");
        endAll();
      }
    });

    ch.on("broadcast", { event: "reject" }, ({ payload }) => {
      const p = payload as { callId: string };
      const cur = infoRef.current;
      if (cur && p.callId === cur.callId) { toast.info("Panggilan ditolak"); endAll(); }
    });

    ch.on("broadcast", { event: "cancel" }, ({ payload }) => {
      const p = payload as { callId: string };
      const cur = infoRef.current;
      if (cur && p.callId === cur.callId) { toast.info("Panggilan dibatalkan"); endAll(); }
    });

    ch.on("broadcast", { event: "end" }, ({ payload }) => {
      const p = payload as { callId: string };
      const cur = infoRef.current;
      if (cur && p.callId === cur.callId) { toast.info("Panggilan berakhir"); endAll(); }
    });

    ch.subscribe();

    return () => {
      supabase.removeChannel(ch);
      inboxRef.current = null;
      for (const c of outCache.current.values()) supabase.removeChannel(c);
      outCache.current.clear();
    };
  }, [me?.id, sendSignal, startTimer, endAll]);

  const runAcceptIncoming = useCallback(async () => {
    const cur = infoRef.current;
    if (!cur || !me || !cur.pendingOffer) return;
    try {
      const stream = await getMedia(cur.mode);
      markGranted();
      const pc = buildPc(cur.mode, cur.callId);
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      await pc.setRemoteDescription(cur.pendingOffer);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await waitIceComplete(pc);
      await sendSignal(cur.peerId, "answer", { callId: cur.callId, sdp: pc.localDescription });
      try { ringRef.current?.pause(); } catch { /* noop */ }
      applyPhase("active");
      startTimer();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error("Gagal menerima panggilan: " + msg);
      const c = infoRef.current;
      if (c) sendSignal(c.peerId, "reject", { callId: c.callId, reason: "error" });
      endAll();
    }
  }, [me, getMedia, buildPc, sendSignal, startTimer, endAll]);

  // Matikan notifikasi panggilan native (dering/getar) di Android.
  const cancelNativeCallNotification = () => {
    try {
      const bridge = (window as unknown as {
        AndroidBridge?: { cancelCallNotification?: () => void };
      }).AndroidBridge;
      bridge?.cancelCallNotification?.();
    } catch {
      // no-op
    }
  };

  const acceptIncoming = () => {
    if (!info) return;
    cancelNativeCallNotification();
    if (grantedRef.current) {
      void runAcceptIncoming();
    } else {
      setPending({ kind: "incoming" });
      applyPhase("permission");
    }
  };

  const handleGrantPermission = () => {
    // Runs inside a user click → getUserMedia popup is allowed
    const p = pending;
    setPending(null);
    if (p?.kind === "outgoing" && info) {
      applyPhase("outgoing");
      void runOutgoing(p.opts, info.callId);
    } else if (p?.kind === "incoming") {
      void runAcceptIncoming();
    }
  };

  const handleDenyPermission = () => {
    const cur = infoRef.current;
    setPending(null);
    cancelNativeCallNotification();
    endAll();
    if (cur && !cur.isCaller) {
      void sendSignal(cur.peerId, "reject", { callId: cur.callId, reason: "denied" });
    }
  };

  const rejectIncoming = async () => {
    const cur = infoRef.current;
    cancelNativeCallNotification();
    endAll();
    if (cur) void sendSignal(cur.peerId, "reject", { callId: cur.callId });
  };
  const cancelOutgoing = async () => {
    const cur = infoRef.current;
    cancelNativeCallNotification();
    endAll();
    if (cur) void sendSignal(cur.peerId, "cancel", { callId: cur.callId });
  };
  const hangup = async () => {
    const cur = infoRef.current;
    cancelNativeCallNotification();
    endAll();
    if (cur) void sendSignal(cur.peerId, "end", { callId: cur.callId });
  };


  // Listener untuk tombol Jawab/Tolak dari notifikasi native Android.
  // MainActivity.java memanggil:
  //   window.dispatchEvent(new CustomEvent('native-call-action',
  //     { detail: { action: 'answer' | 'decline', roomId, mode } }))
  // Saat cold-start (app dibuka dari notifikasi), MainActivity juga menaruh
  // aksi terakhir di window.lastCallAction agar tidak hilang sebelum listener
  // ini terpasang.
  useEffect(() => {
    const handleDetail = (detail: { action: string; roomId?: string } | null | undefined) => {
      if (!detail) return;
      const tryHandle = (attempt = 0) => {
        const cur = infoRef.current;
        // Panggilan masuk mungkin belum sampai (invite realtime belum tiba).
        // Coba ulang hingga ~8 detik saat cold-start.
        if (!cur || phaseRef.current !== "incoming") {
          if (attempt < 40) {
            window.setTimeout(() => tryHandle(attempt + 1), 200);
          }
          return;
        }
        if (detail.roomId && cur.callId !== detail.roomId) return;
        if (detail.action === "answer") {
          cancelNativeCallNotification();
          // Beri jeda kecil agar WebRTC/PeerConnection sepenuhnya siap.
          window.setTimeout(() => {
            if (phaseRef.current !== "incoming") return;
            if (grantedRef.current) {
              void runAcceptIncoming();
            } else {
              setPending({ kind: "incoming" });
              applyPhase("permission");
            }
          }, 300);
        } else if (detail.action === "decline") {
          cancelNativeCallNotification();
          void sendSignal(cur.peerId, "reject", { callId: cur.callId });
          endAll();
        }

      };
      tryHandle();
    };

    const onNativeAction = (ev: Event) => {
      const detail = (ev as CustomEvent<{ action: string; roomId?: string }>).detail;
      handleDetail(detail);
    };
    window.addEventListener("native-call-action", onNativeAction as EventListener);

    // Cold-start: MainActivity mungkin sudah menaruh aksi sebelum listener siap.
    const w = window as unknown as { lastCallAction?: { action: string; roomId?: string } | null };
    if (w.lastCallAction) {
      const pending = w.lastCallAction;
      w.lastCallAction = null;
      handleDetail(pending);
    }

    return () => window.removeEventListener("native-call-action", onNativeAction as EventListener);
  }, [runAcceptIncoming, sendSignal, endAll, applyPhase]);


  const toggleMute = () => {
    const s = localStreamRef.current; if (!s) return;
    const enabled = !muted; // if muted, want to unmute
    s.getAudioTracks().forEach((t) => { t.enabled = enabled ? false : true; });
    setMuted((v) => !v);
  };
  const toggleCam = () => {
    const s = localStreamRef.current; if (!s) return;
    s.getVideoTracks().forEach((t) => { t.enabled = camOff ? true : false; });
    setCamOff((v) => !v);
  };

  const switchCamera = async () => {
    if (switchingCam) return;
    const s = localStreamRef.current; if (!s) return;
    const pc = pcRef.current;
    const next = facingModeRef.current === "user" ? "environment" : "user";
    setSwitchingCam(true);
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: { ideal: next } },
      });
      const newTrack = newStream.getVideoTracks()[0];
      if (!newTrack) throw new Error("Kamera lain tidak tersedia.");

      // Replace outgoing video track on the peer connection
      if (pc) {
        const sender = pc.getSenders().find((se) => se.track && se.track.kind === "video");
        if (sender) await sender.replaceTrack(newTrack);
      }

      // Swap the video track on the local MediaStream
      const oldTrack = s.getVideoTracks()[0];
      if (oldTrack) { s.removeTrack(oldTrack); oldTrack.stop(); }
      s.addTrack(newTrack);

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = s;
        localVideoRef.current.play().catch(() => { /* noop */ });
      }
      facingModeRef.current = next;
      setFacingMode(next);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Gagal mengganti kamera.");
    } finally {
      setSwitchingCam(false);
    }
  };

  const toggleSpeaker = async () => {
    const audioEl = remoteAudioRef.current;
    const videoEl = remoteVideoRef.current;
    const next = !speakerOn;
    setSpeakerOn(next);

    // Volume boost as universal fallback (works on all browsers, incl. mobile)
    if (audioEl) audioEl.volume = next ? 1 : 0.35;
    if (videoEl) videoEl.volume = next ? 1 : 0.35;

    // Try to actually switch output device on browsers that support setSinkId (Chrome/Edge desktop)
    type SinkAudio = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
    const el = audioEl as SinkAudio | null;
    if (el?.setSinkId && navigator.mediaDevices?.enumerateDevices) {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const outs = devices.filter((d) => d.kind === "audiooutput");
        // Heuristic: pick a device whose label mentions speaker when speakerOn, else default/earpiece
        const wantLabel = next ? /speaker|loud|external/i : /earpiece|receiver|internal|default/i;
        const target = outs.find((d) => wantLabel.test(d.label)) || outs.find((d) => d.deviceId === "default");
        if (target) await el.setSinkId(target.deviceId);
      } catch { /* setSinkId unsupported or blocked → volume fallback already applied */ }
    }
  };

  if (phase === "idle" || !info) {
    return <audio ref={remoteAudioRef} className="hidden" />;
  }

  if (phase === "permission") {
    const wantVideo = info.mode === "video";
    return (
      <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="w-full max-w-sm rounded-2xl bg-background text-foreground shadow-2xl p-6 space-y-4 animate-in fade-in zoom-in-95">
          <div className="flex items-center justify-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/15 flex items-center justify-center text-primary">
              {wantVideo ? <Video className="w-6 h-6" /> : <Phone className="w-6 h-6" />}
            </div>
            {wantVideo && (
              <div className="h-12 w-12 rounded-full bg-primary/15 flex items-center justify-center text-primary">
                <Mic className="w-6 h-6" />
              </div>
            )}
          </div>
          <div className="text-center space-y-1">
            <h3 className="text-lg font-bold">
              Izinkan akses {wantVideo ? "kamera & mikrofon" : "mikrofon"}
            </h3>
            <p className="text-sm text-muted-foreground">
              {pending?.kind === "incoming"
                ? <>Untuk menerima panggilan {wantVideo ? "video" : "suara"} dari <b>{info.peerName}</b>, aplikasi butuh akses {wantVideo ? "kamera & mic" : "mic"} kamu.</>
                : <>Untuk memulai panggilan {wantVideo ? "video" : "suara"} ke <b>{info.peerName}</b>, aplikasi butuh akses {wantVideo ? "kamera & mic" : "mic"} kamu.</>}
            </p>
            <p className="text-xs text-muted-foreground pt-1">
              Browser akan menampilkan konfirmasi. Klik <b>Allow</b> agar panggilan bisa dimulai.
            </p>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={handleDenyPermission}>
              Batal
            </Button>
            <Button className="flex-1" onClick={handleGrantPermission}>
              Izinkan
            </Button>
          </div>
        </div>
      </div>
    );
  }


  const durText = (() => {
    const s = Math.floor(elapsed / 1000);
    const m = Math.floor(s / 60); const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  })();

  // Floating minimized widget (WhatsApp-style) — only while call is active
  if (phase === "active" && minimized) {
    return (
      <>
        <audio ref={remoteAudioRef} className="hidden" autoPlay playsInline />
        {info.mode === "video" && (
          <video ref={remoteVideoRef} autoPlay playsInline className="hidden" />
        )}
        {info.mode === "video" && (
          <video ref={localVideoRef} autoPlay playsInline muted className="hidden" />
        )}
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="fixed bottom-20 right-4 z-[100] flex items-center gap-3 pl-2 pr-4 py-2 rounded-full bg-emerald-600 text-white shadow-2xl hover:bg-emerald-700 transition-all animate-in slide-in-from-bottom-4"
          aria-label="Kembalikan panggilan"
        >
          <Avatar className="w-10 h-10 ring-2 ring-white/40">
            {info.peerAvatar && <AvatarImage src={info.peerAvatar} />}
            <AvatarFallback className="bg-emerald-800 text-white text-sm font-bold">{initialsOf(info.peerName)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start leading-tight">
            <span className="text-sm font-semibold max-w-[10rem] truncate">{info.peerName}</span>
            <span className="text-xs text-white/85 flex items-center gap-1">
              {info.mode === "video" ? <Video className="w-3 h-3" /> : <Phone className="w-3 h-3" />}
              {durText}
            </span>
          </div>
          <Maximize2 className="w-4 h-4 ml-1 opacity-90" />
        </button>
      </>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] text-white flex flex-col overflow-hidden">
      {/* Ambient background — deep emerald gradient with soft glow, no giant play icon */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, oklch(0.42 0.13 162) 0%, oklch(0.22 0.06 160) 55%, oklch(0.14 0.03 160) 100%)",
        }}
      />
      {/* Blurred avatar halo for a premium feel */}
      {info.peerAvatar && (
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(${info.peerAvatar})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(40px) saturate(1.2)",
            transform: "scale(1.2)",
          }}
        />
      )}
      <div className="absolute inset-0 bg-black/35" />

      {/* Ringtone (silent placeholder; using vibration/no audio to keep it simple) */}
      <audio ref={ringRef} loop className="hidden" />
      <audio ref={remoteAudioRef} className="hidden" autoPlay playsInline />

      {/* Minimize button — only during active call */}
      {phase === "active" && (
        <button
          type="button"
          onClick={() => setMinimized(true)}
          className="absolute top-4 left-4 z-20 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center text-white"
          aria-label="Kecilkan panggilan"
        >
          <Minimize2 className="w-5 h-5" />
        </button>
      )}


      {/* Remote video — only mount during active call so an empty <video> doesn't
          show the browser's giant play-button poster during incoming/outgoing. */}
      {info.mode === "video" && phase === "active" && (
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="absolute inset-0 w-full h-full object-cover bg-black"
        />
      )}
      {info.mode === "video" && phase !== "active" && (
        <video ref={remoteVideoRef} autoPlay playsInline className="hidden" />
      )}

      {/* Overlay */}
      <div className="relative z-10 flex flex-col items-center justify-between h-full p-6">
        <div className="flex flex-col items-center gap-3 mt-8 text-center">
          <Avatar className="w-24 h-24 ring-4 ring-white/20">
            {info.peerAvatar && <AvatarImage src={info.peerAvatar} />}
            <AvatarFallback className="bg-primary/30 text-white text-2xl font-bold">{initialsOf(info.peerName)}</AvatarFallback>
          </Avatar>
          <div className="text-xl font-bold drop-shadow">{info.peerName}</div>
          <div className="text-sm text-white/80 drop-shadow">
            {phase === "outgoing" && "Memanggil..."}
            {phase === "incoming" && (info.mode === "video" ? "Panggilan video masuk" : "Panggilan suara masuk")}
            {phase === "active" && durText}
          </div>
        </div>

        {/* Local preview */}
        {info.mode === "video" && phase === "active" && (
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            style={facingMode === "user" ? { transform: "scaleX(-1)" } : undefined}
            className="absolute top-4 right-4 w-28 h-40 object-cover rounded-xl border-2 border-white/40 shadow-lg bg-black"
          />
        )}
        {info.mode === "video" && phase !== "active" && (
          <video ref={localVideoRef} autoPlay playsInline muted className="hidden" />
        )}

        {/* Controls */}
        <div className="pb-6 flex items-center justify-center gap-4">
          {phase === "incoming" ? (
            <>
              <Button size="lg" onClick={rejectIncoming} className="h-16 w-16 rounded-full bg-red-600 hover:bg-red-700">
                <PhoneOff className="w-6 h-6" />
              </Button>
              <Button size="lg" onClick={acceptIncoming} className="h-16 w-16 rounded-full bg-emerald-600 hover:bg-emerald-700">
                {info.mode === "video" ? <Video className="w-6 h-6" /> : <Phone className="w-6 h-6" />}
              </Button>
            </>
          ) : phase === "outgoing" ? (
            <Button size="lg" onClick={cancelOutgoing} className="h-16 w-16 rounded-full bg-red-600 hover:bg-red-700">
              <PhoneOff className="w-6 h-6" />
            </Button>
          ) : (
            <>
              <Button size="lg" variant="secondary" onClick={toggleMute} className="h-14 w-14 rounded-full">
                {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </Button>
              {info.mode === "video" && (
                <Button size="lg" variant="secondary" onClick={toggleCam} className="h-14 w-14 rounded-full">
                  {camOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
                </Button>
              )}
              {info.mode === "video" && (
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={switchCamera}
                  disabled={switchingCam || camOff}
                  className="h-14 w-14 rounded-full"
                  aria-label="Ganti kamera"
                >
                  <SwitchCamera className="w-5 h-5" />
                </Button>
              )}
              <Button
                size="lg"
                variant="secondary"
                onClick={toggleSpeaker}
                className={`h-14 w-14 rounded-full ${speakerOn ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}
                aria-label={speakerOn ? "Matikan loudspeaker" : "Aktifkan loudspeaker"}
              >
                {speakerOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </Button>
              <Button size="lg" onClick={hangup} className="h-16 w-16 rounded-full bg-red-600 hover:bg-red-700">
                <PhoneOff className="w-6 h-6" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
});
